import React from 'react';
import styled, { css } from 'styled-components/macro';
import { ThemeProps } from 'types/styled';
import SearchableMenu from 'components/base/Menu/SearchableMenu';
import { ReactComponent as infoIcon } from 'icons/icon-info.svg';

import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';
import Span from 'components/elements/span';
import Box from 'components/elements/Box';
import colors from 'styles/themes/colors';
import Menu from '../../Menu/Menu';
import ReleaseIcon from './ReleaseIcon';

import { ListItemBoxBase } from 'components/base/Dropdown/shared/styles';
import DropdownButton from '../DropdownButton';
import DropdownWrapper from '../DropdownWrapper';

const FlagImage = styled.img<ThemeProps<'img'>>`
  display: inline-flex;
`;

const ListWrapper = styled(Box)<any>`
  ${ListItemBoxBase}
  padding: ${({ theme }): string => `0.7rem ${theme.spacing.medium}`};
  min-width: 175px;
  justify-content: space-between;

  &:hover,
  &[aria-selected='true'] {
    background: ${colors.Sand};
  }

  ${({ isActive }): any => css`
    cursor: pointer;
    background: ${isActive ? colors.Sand : 'white'};
  `}
`;

const ListItem: React.FC<IListItemProps> = (props) => {
  const { item, isActive, isSmallScreen } = props;
  return (
    <ListWrapper
      isActive={isActive}
      display="flex"
      alignItems="center"
      alignContent="stretch"
      py={2}
      paddingRight={2}
      paddingLeft={3}
    >
      <Box height="25px" display="flex">
        <Box
          alignSelf="auto"
          display="inline-block"
          minWidth="2rem"
          maxHeight="1rem"
          paddingLeft={3.5}
        >
          {item.name && (
            <ReleaseIcon small aria-hidden releaseType={item.name} />
          )}
        </Box>
        <Span marginLeft={1} alignSelf="auto">
          {item.label}
        </Span>
      </Box>

      {!isSmallScreen && (
        <TippyToolTip
          tooltipContent={item.tooltipContent}
          maxWidth="222px"
          enabled
        >
          <Box
            alignSelf="center"
            alignItems="center"
            height="25px"
            display="flex"
          >
            <FlagImage as={infoIcon} width={16} height={16} marginRight={0} />
          </Box>
        </TippyToolTip>
      )}
    </ListWrapper>
  );
};

const getButtonWithDefaultLabel = (
  clearFilter: () => void,
  defaultLabel?: string
): React.FC<any> => {
  const LabeledButton: React.FC<any> = (props: any) => {
    return (
      <DropdownButton
        {...props}
        defaultLabel={defaultLabel}
        clearFilter={clearFilter}
        dropdownType="Release"
      />
    );
  };
  return LabeledButton;
};

const ReleaseDropdown: React.FC<IReleaseDropdownProps> = (props) => {
  const {
    items,
    selectedItem,
    onChange,
    disabled,
    itemToString,
    isSmallScreen,
    defaultLabel,
    children,
    isTooltipEnabled,
    tooltipContent,
    clearFilter,
  } = props;

  if (disabled) {
    return (
      <div>
        <DropdownWrapper
          hasWideSearchableMenu
          isEnabled={!disabled}
          label="TIME PERIOD"
        >
          <SearchableMenu
            items={items}
            selectedItem={selectedItem || null}
            onChange={onChange}
            disabled={disabled}
            itemToString={itemToString}
            Button={getButtonWithDefaultLabel(clearFilter, defaultLabel)}
            Item={ListItem}
            isSmallScreen={isSmallScreen}
            showListLabel
            disabledMessage={'Only available for Tracks and Releases'}
            isTooltipEnabled={isTooltipEnabled}
            tooltipContent={tooltipContent}
          >
            {children}
          </SearchableMenu>
        </DropdownWrapper>
      </div>
    );
  }

  // selectedItem can be undefined which will trigger React into thinking this is a controlled component,
  // when it then switches to an actual value runtime errors will be thrown since React will figure
  // we're trying to switch a controlled component into an uncontrolled component. Null is a valid value,
  // therefore we add this seemingly weird check.
  return (
    <DropdownWrapper label="TIME PERIOD">
      <Menu
        items={items}
        selectedItem={selectedItem || null}
        onChange={onChange}
        disabled={disabled}
        itemToString={itemToString}
        Button={getButtonWithDefaultLabel(clearFilter, defaultLabel)}
        Item={ListItem}
        isTooltipEnabled={isTooltipEnabled}
        tooltipContent={tooltipContent}
        maxWidth="175px"
      >
        {children}
      </Menu>
    </DropdownWrapper>
  );
};

interface IReleaseDropdownProps {
  items: any[];
  selectedItem: any;
  onChange: (item: any) => void;
  disabled?: boolean;
  itemToString?: (item: any) => string | null;
  defaultLabel?: string;
  children?: any;
  isTooltipEnabled?: boolean;
  tooltipContent?: string | JSX.Element;
  isSmallScreen?: boolean;
  clearFilter: () => void;
}

interface IListItemProps {
  item: any;
  isActive?: boolean;
  isSmallScreen?: boolean;
}

export default ReleaseDropdown;
